// ============================================================================
// MemoryTabHipocampo — MS-09
// ----------------------------------------------------------------------------
// Aba "Hipocampo OS" dentro de /memory.
//   • Diagrama animado das 5 layers (Working/Short/Long/People/Procedural)
//   • Selector de auto-refresh (Off/15s/30s/1min/5min) + cache TTL
//   • Lista cronológica das últimas pulsações (drives da animação)
//
// Read-only. Usa hook compartilhado useHipocampoFlow.
// ============================================================================

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Loader2, RefreshCw } from "lucide-react";
import { useHipocampoFlow, type HipocampoLayer } from "@/hooks/useHipocampoFlow";
import { HipocampoFlow } from "@/components/memory/HipocampoFlow";
import { FreshnessIndicator } from "@/components/states/FreshnessIndicator";

const REFRESH_OPTIONS = [
  { label: "Desligado", value: 0 },
  { label: "15s", value: 15_000 },
  { label: "30s", value: 30_000 },
  { label: "1min", value: 60_000 },
  { label: "5min", value: 300_000 },
];
const REFRESH_DEFAULT = 30_000;
const REFRESH_STORAGE_KEY = "memory.hipocampo.refresh-ms";

const WINDOW_OPTIONS = [
  { label: "Última 1h", value: 1 },
  { label: "Últimas 24h", value: 24 },
  { label: "Últimos 7d", value: 24 * 7 },
];
const WINDOW_DEFAULT = 24;
const WINDOW_STORAGE_KEY = "memory.hipocampo.pulse-window-hours";

const LAYER_LABEL: Record<HipocampoLayer, string> = {
  working: "Working",
  short: "Short",
  long: "Long",
  people: "People",
  procedural: "Procedural",
};

const LAYER_BADGE_CLASS: Record<HipocampoLayer, string> = {
  working: "border-primary/40 text-primary",
  short: "border-accent text-accent-foreground",
  long: "border-success/40 text-success",
  people: "border-warning/40 text-warning",
  procedural: "border-secondary text-secondary-foreground",
};

export default function MemoryTabHipocampo() {
  const [intervalMs, setIntervalMs] = useState<number>(() => {
    if (typeof window === "undefined") return REFRESH_DEFAULT;
    const raw = window.localStorage.getItem(REFRESH_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && REFRESH_OPTIONS.some((o) => o.value === n) ? n : REFRESH_DEFAULT;
  });
  const [windowHours, setWindowHours] = useState<number>(() => {
    if (typeof window === "undefined") return WINDOW_DEFAULT;
    const raw = window.localStorage.getItem(WINDOW_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && WINDOW_OPTIONS.some((o) => o.value === n) ? n : WINDOW_DEFAULT;
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(REFRESH_STORAGE_KEY, String(intervalMs));
    }
  }, [intervalMs]);
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(WINDOW_STORAGE_KEY, String(windowHours));
    }
  }, [windowHours]);

  const flow = useHipocampoFlow(intervalMs, undefined, windowHours);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Brain className="h-4 w-4" /> Hipocampo OS — Fluxo
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Diagrama em tempo real das 5 layers de memória. Os nós pulsam
                quando há atividade nova; arestas brilham quando uma transição
                aconteceu nos últimos 30s.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <FreshnessIndicator updatedAt={flow.lastUpdatedAt} prefix="Atualizada há" />
              <div className="w-36">
                <Select value={String(windowHours)} onValueChange={(v) => setWindowHours(Number(v))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WINDOW_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        Pulsos: {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Select value={String(intervalMs)} onValueChange={(v) => setIntervalMs(Number(v))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REFRESH_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        Auto: {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => flow.refetch({ force: true })}
                disabled={flow.loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${flow.loading ? "animate-spin" : ""}`} />
                Recarregar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {flow.loading && !flow.lastUpdatedAt && (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando fluxo do hipocampo…
            </div>
          )}
          {flow.error && <p className="text-sm text-destructive">Erro: {flow.error}</p>}
          {(!flow.loading || flow.lastUpdatedAt) && !flow.error && (
            <HipocampoFlow layers={flow.layers} pulses={flow.pulses} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Últimas pulsações (timeline)</CardTitle>
        </CardHeader>
        <CardContent>
          {flow.pulses.length === 0 ? (
            <p className="text-xs text-muted-foreground">Sem atividade recente.</p>
          ) : (
            <ul className="space-y-1.5 max-h-[480px] overflow-auto">
              {flow.pulses.map((p, i) => (
                <li key={`${p.layer}-${p.at}-${i}`} className="text-xs flex items-center gap-2 border-b border-border/50 pb-1.5 last:border-b-0">
                  <Badge variant="outline" className={`text-[10px] uppercase ${LAYER_BADGE_CLASS[p.layer]}`}>
                    {LAYER_LABEL[p.layer]}
                  </Badge>
                  <span className="font-mono text-[11px] truncate">{p.label}</span>
                  <span className="ml-auto text-muted-foreground text-[11px] tabular-nums">
                    {new Date(p.at).toLocaleString("pt-BR")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
